import {
    BROKEN_COOKIE_VISITOR_ID,
    COOKIE_EXPIRATION_DAYS,
    COOKIE_MARKETPLACE_VIEW,
    COOKIE_VISITOR_ID,
    OC_SHOW_WELCOME_MODALS_COOKIE_NAME,
} from '@marketplace/config/cookie.js'
import { GET_STOREFRONTS } from '@marketplace/graphql/dropshipping.js'
import { viewerQuery } from '@marketplace/graphql/viewer.js'
import { VISIT_MUTATION } from '@marketplace/graphql/visit.js'
import { getFullUrlFromRoute } from '@marketplace/helpers/router.js'
import { identifyWithTraitsPromise } from '@marketplace/helpers/segment/index.js'
import { EventBus } from '@marketplace/plugins/event-bus.js'
import Cookies from 'js-cookie'
import debounce from 'lodash/debounce'
import { v4 as uuidv4 } from 'uuid'

const initializedEvent = new Event('client:initialized')

export const actions = {
    async nuxtClientInit({ dispatch, getters }, context) {
        await dispatch('getViewer', context)

        dispatch(
            'marketplace/updateCategories',
            window.Store.marketplace.categories
        )
        dispatch('marketplace/updateFilters', window.Store.marketplace.filters)
        dispatch(
            'marketplace/updateDsFilters',
            window.Store.marketplace.ds_filters
        )
        dispatch(
            'marketplace/updateStorefrontFilters',
            window.Store.marketplace.storefront_filters
        )
        dispatch('marketplace/updateSorts', window.Store.marketplace.sorts)
        dispatch(
            'marketplace/updateSeoPages',
            window.Store.marketplace.seo_pages
        )
        dispatch(
            'marketplace/updateNavigation',
            window.Store.marketplace.navigation
        )

        dispatch('flattenCategoriesToVuex')
        dispatch('updateAppBreakpoint')
        dispatch('setVisit', context)
        dispatch('initSentry')
        dispatch('messages/setPersistentMessage')
        dispatch('initializeCookieViewMode', context)

        setTimeout(() => {
            dispatch('openEducationModal')
        }, 1000)

        await window.dispatchEvent(initializedEvent)

        // Should run after the client:initialized event
        dispatch('identify', context)
    },

    async getViewer({ commit }, context) {
        const client = context.app.apolloProvider.defaultClient
        await client
            .query({
                query: viewerQuery,
                fetchPolicy: 'no-cache',
            })
            .then(async ({ data }) => {
                const revision = data?.revision
                const intendedUrl = data?.intendedUrl
                const account = data?.viewer?.account
                const hasBoughtAnOrder =
                    +data?.viewer?.retailerInvoices?.totalCount > 0 ||
                    +data?.viewer?.account?.hasPreviouslyPurchased

                window.Store = JSON.parse(data.head)

                commit('app/updateIntendedUrl', intendedUrl)

                if (account) {
                    const experimentGroup = account.experimentGroup || 0

                    commit(
                        'global/updateHasBoughtAnOrder',
                        hasBoughtAnOrder || false
                    )

                    commit(
                        'ocplus/updateOcPlusState',
                        account.hasOrderchampPlus
                    )

                    commit('global/updateUserData', {
                        databaseId: account.databaseId,
                        hasPayAfterArrival: account.hasPayAfterArrival,
                        updateExperimentGroup: experimentGroup,
                        email: data?.viewer?.account?.email,
                        phone: data?.viewer?.account?.phone,
                        userFirstName: data?.viewer?.user?.firstName,
                        userEmail: data?.viewer?.user?.email,
                        userLastVisitedEnvironment:
                            data?.viewer?.user?.lastVisitedEnvironment,
                        userId: account.databaseId,

                        registeredCompanyName: account.registeredCompanyName,
                        retailerType: account.retailerType,
                        signupStatus: account.signupStatus,
                        approvalStatus: account.approvalStatus,
                        ticaMatches: account.ticaMatches,
                        ticaRetailer: account.ticaRetailer,
                        type: account.type,
                        unpaidRetailerOrder: data?.viewer?.unpaidRetailerOrder,
                        vatNumber: account?.vatNumber,
                        cocNumber: account?.cocNumber,
                        hasValidVatNumber: account?.hasValidVatNumber,
                        isExcludedFromTracking:
                            data?.viewer?.isExcludedFromTracking,
                        dropshipping: account?.dropshipping,
                        isEligibleForDropshipping:
                            account?.isEligibleForDropshipping,
                        subscription: account?.subscription,
                        channels: account?.channels,
                        signupIntent: account?.signupIntent,
                        vouchers: data?.viewer?.account?.vouchers,
                        hideEmailForMarketplace:
                            account.hideEmailForMarketplace,
                        hideEmailForDropshipping:
                            account.hideEmailForDropshipping,
                        shouldShowTrademartPartnershipModal:
                            account.shouldShowTrademartPartnershipModal,
                        requestCountry: data?.viewer?.request?.country,
                        isSmallBusiness: account?.isSmallBusiness,
                        // Address
                        houseNumber: account.houseNumber,
                        postalCode: account.postalCode,
                        street: account.street,
                        country: account.country,
                        canStartConversations: account.canStartConversations,
                        instagramUsername: account?.instagramUsername,
                        url: account?.url,
                        dropshippingInvitedByStorefront:
                            account?.dropshippingInvitedByStorefront,
                    })

                    commit('feature/updateFeatures', account?.features)

                    commit('global/updatePreferences', {
                        softwareEcom: account.preferences?.softwareEcom,
                        softwarePos: account.preferences?.softwarePos,
                    })

                    commit(
                        'global/updatePaymentMethods',
                        data?.viewer?.visiblePaymentMethods
                    )

                    commit('app/updateRevision', revision)
                    commit(
                        'global/updateCartProductsCount',
                        data.viewer?.totalProductsInCartCount
                    )
                    commit('global/updateInboxUnreadCount', account.unreadCount)

                    commit(
                        'dropshipping/setCount',
                        account.unreadDropshippingNotifications
                    )
                    if (account.features?.includes('no-ds-marketplace')) {
                        const { data: storefrontData } = await client.query({
                            query: GET_STOREFRONTS,
                            variables: { first: 20, page: 1 },
                            fetchPolicy: 'no-cache',
                        })

                        const storefronts =
                            storefrontData?.viewer?.account
                                ?.retailerDropshippingStorefronts?.results || []

                        commit('ds/setDsStorefronts', storefronts)
                    }
                }
            })
    },

    async flattenCategoriesToVuex({ dispatch }) {
        const categories = window.Store.marketplace.categories
        await dispatch('categories/flattenCategoriesToPathObject', categories)
    },

    updateAppBreakpoint({ commit }) {
        /**
         * Get the named breakpoint that's used in css.
         *
         * @returns {string}
         */
        function getBreakpoint() {
            const string = window.getComputedStyle(
                document.body,
                ':before'
            ).content
            const breakpoint = string.replace(/[^\w\s]/gi, '')

            return breakpoint
        }

        commit('global/updateAppBreakPoint', getBreakpoint())

        window.addEventListener(
            'resize',
            debounce(() => {
                commit('global/updateAppBreakPoint', getBreakpoint())
            }),
            150
        )
    },

    openEducationModal({ dispatch }) {
        if (Cookies.get(OC_SHOW_WELCOME_MODALS_COOKIE_NAME)) {
            EventBus.$emit('triggerEducationModal')
        }
    },

    setVisit({ commit }, context) {
        const currentUrl = getFullUrlFromRoute(context.route)
        const referrerUrl = document.referrer
        let visitorId = Cookies.get(COOKIE_VISITOR_ID)
        const brokenVisitorId = Cookies.get(BROKEN_COOKIE_VISITOR_ID)

        if (!visitorId || brokenVisitorId) {
            visitorId = uuidv4()

            if (brokenVisitorId) {
                visitorId = brokenVisitorId
                Cookies.remove(BROKEN_COOKIE_VISITOR_ID, {
                    path: '/', // Specifically set the path because the defaults aren't used when updating a cookie
                })
            }

            Cookies.set(COOKIE_VISITOR_ID, visitorId, {
                expires: COOKIE_EXPIRATION_DAYS.ONE_YEAR,
                path: '/', // Specifically set the path because the defaults aren't used when updating a cookie
                domain: context.$config.cookieUrl,
            })
        }

        const client = context.app.apolloProvider.defaultClient
        client
            .mutate({
                mutation: VISIT_MUTATION,
                variables: {
                    input: {
                        visitorId,
                        landingUrl: currentUrl,
                        referrerUrl,
                    },
                },
            })
            .catch((error) => {
                this.$sentry.withScope((scope) => {
                    scope.setTag('errorType', 'GraphQL Error')
                    scope.setTag('errorType', 'Visit Mutation Failed')

                    // Adding context about the mutation that failed
                    scope.setContext('GraphQL Mutation Details', {
                        mutation: 'VISIT_MUTATION',
                        visitorId,
                        landingUrl: currentUrl,
                        referrerUrl,
                    })

                    // Capture the exception with Sentry
                    this.$sentry.captureException(error)
                })
            })
    },

    identify() {
        window
            .OCsessionStart()
            .then(() => identifyWithTraitsPromise(false))
            .catch(() => {
                console.info('User tracking is off')
            })
    },

    initSentry() {
        this.$sentry.setUser({
            id: window.Store.accountId || '',
            username: window.Store.accountName || '',
        })

        this.$sentry.setContext('Orderchamp', {
            accountName: window.Store.accountName || '',
            accountId: window.Store.accountId || '',
            accountType: window.Store.accountType || '',
            userId: window.Store.userId || '',
        })
    },
    initializeCookieViewMode({ dispatch, state }) {
        const breakpoint = state.global.appBreakPoint

        // Determine the view modes for marketplace
        const marketplaceViewMode =
            breakpoint && breakpoint.includes('touch') ? 'wall' : 'listings'

        // Initialize the view modes for marketplace
        dispatch('setCookieViewModeAndPersist', {
            value: Cookies.get(COOKIE_MARKETPLACE_VIEW) || marketplaceViewMode,
        })
    },

    setCookieViewModeAndPersist({ commit }, { value }) {
        const cookieName = COOKIE_MARKETPLACE_VIEW
        const mutationName = 'global/setPreferredView'
        const validViews = ['listings', 'wall', 'marketplace']
        let viewToSet = value

        if (!validViews.includes(value)) {
            this.$sentry.withScope((scope) => {
                scope.setTag('errorType', 'Invalid View Mode')
                scope.setLevel('warning')
                scope.setContext('Cookie Details', {
                    cookieName,
                    attemptedValue: value,
                    validViews,
                })
                this.$sentry.captureException(
                    new Error(`Invalid marketplace view mode: "${value}"`)
                )
            })
            viewToSet = 'listings'
        }

        Cookies.set(cookieName, viewToSet, {
            expires: COOKIE_EXPIRATION_DAYS.ONE_YEAR,
        })
        commit(mutationName, viewToSet)
    },
}
